import { HostListener, Injectable, OnDestroy } from '@angular/core';
import { Util } from '@phdsoft/common';
import { AssetManager, FunctionalityMode } from './asset-manager';
import { CollectionStaticData } from '../collections/_utils/collection.static.data';
import { WorkableElement } from '../platform/busines-data/SectionElements/WorkableElement';
import { GraphicsMaterials } from '../digital-twin/graphics/graphics-materials';
import { v4 as uuid } from 'uuid';
import { Graphics } from '../digital-twin/graphics/graphics';
import { CriticalAreaCampaign } from '../platform/busines-data/Campaigns/CriticalAreaCampaign';
import { SurfaceElement } from '../platform/busines-data/SectionElements/StructureElements/SurfaceElement';
import { LinearElement } from '../platform/busines-data/SectionElements/StructureElements/LinearElement';
import { mergeVertices } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import * as THREE from '@phdsoft/three';
import { DigitalTwinService } from '../digital-twin/digital-twin.service';
import { GraphicsRenderCriticalArea } from '../digital-twin/graphics/graphics-render-critical-area';
import { GraphicsRenderRepair } from '../digital-twin/graphics/graphics-render-repair';
import { CriticalAreaElement } from '../platform/busines-data/CriticalAreaElement/CriticalAreaElement';
import { DTSelectionService } from '../digital-twin/selection/selection.service';
import { getUVSValues } from '../digital-twin/MeshUtils/DataTextureUtils';
import { CriticalAreaService, ICreateCriticalAreasRequestModelDTO, ICriticalAreaDTO, ICriticalAreaElementDTO } from '../template/critical-area-window/critical-area.service';
import { CriticalAreaModel } from '../template/critical-area-window/critical-area.model';
import { MatDialog } from '@angular/material/dialog';
import { CriticalAreaBoxConfigComponent } from '../template/critical-area-box-config/critical-area-box-config.component';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AssetManagerFilter } from './asset-manager-filter';
import { RiskMatrixService } from '../admin/pages/risk-setup/service/risk-matrix.service';
import { firstValueFrom } from 'rxjs';
import riskMatrixUtils from '../admin/pages/risk-setup/risk-matrix-config/utils/risk-matrix.utils';
import { TemplateService } from '@app/template/template.service';
import { CriticalAreaCommandService } from '@app/shared/command/critical-area/critical-area-command.service';
import { Asset } from '../platform/busines-data/Framework/Asset';

export type RegionCriticalAreaReadinessState = 'loading' | 'ready' | 'failed';

export interface RegionCriticalAreaReadiness {
  readonly assetId: string;
  readonly regionId: string;
  readonly asset: Asset;
  readonly generation: number;
  readonly promise: Promise<void>;
  state: RegionCriticalAreaReadinessState;
  error?: unknown;
}

interface MutableRegionCriticalAreaReadiness extends RegionCriticalAreaReadiness {
  pendingExecutions: number;
  hasFailure: boolean;
  resolve: () => void;
  reject: (error: unknown) => void;
}

@Injectable({ providedIn: 'root' })
export class AssetManagerCriticalArea implements OnDestroy {

  private readonly regionCriticalAreaReadiness = new Map<string, MutableRegionCriticalAreaReadiness>();
  private regionCriticalAreaReadinessGeneration = 0;

  //#region Centralized getters/setters using asset-manager
  private get _currentCriticalAreaCampaign(): CriticalAreaCampaign {
    return this.assetManager._currentCriticalAreaCampaign;
  }

  private set _currentCriticalAreaCampaign(value: CriticalAreaCampaign) {
    this.assetManager._currentCriticalAreaCampaign = value;
  }

  private get functionalityMode(): FunctionalityMode {
    return this.assetManager.functionalityMode;
  }

  private set functionalityMode(value: FunctionalityMode) {
    this.assetManager.functionalityMode = value;
  }

  public get criticalAreaBox(): THREE.Mesh<THREE.BoxGeometry, THREE.MeshStandardMaterial> {
    return this.assetManager.criticalAreaBox
  }
  public set criticalAreaBox(box: THREE.Mesh<THREE.BoxGeometry, THREE.MeshStandardMaterial>) {
    this.assetManager.criticalAreaBox = box
  }

  //#endregion

  constructor(
    private graphicsRenderCriticalArea: GraphicsRenderCriticalArea,
    private graphicsRenderRepair: GraphicsRenderRepair,
    private graphics: Graphics,
    private assetManager: AssetManager,
    private criticalAreaService: CriticalAreaService,
    private digitalTwinService: DigitalTwinService,
    public dialog: MatDialog,
    public snackBar: MatSnackBar,
    public assetManagerFilter: AssetManagerFilter,
    public riskMatrixService: RiskMatrixService,
    public templateService: TemplateService,
    // private criticalAreaCommandService: CriticalAreaCommandService,
  ) {
  }

  @HostListener('unloaded')
  ngOnDestroy(): void {
    this.regionCriticalAreaReadiness.clear();
    console.log('💣 Destroy AssetManagerCriticalArea');
  }

  public registerRegionCriticalAreaReadiness(
    assetId: string,
    regionId: string,
    execution: Promise<unknown>,
    asset: Asset
  ): RegionCriticalAreaReadiness {
    const key = this.getRegionCriticalAreaReadinessKey(assetId, regionId);
    let readiness = this.regionCriticalAreaReadiness.get(key);

    if (!readiness || readiness.asset !== asset || readiness.state !== 'loading') {
      readiness = this.createRegionCriticalAreaReadiness(assetId, regionId, asset);
      this.regionCriticalAreaReadiness.set(key, readiness);
    }

    readiness.pendingExecutions += 1;
    Promise.resolve(execution).then(
      () => this.completeRegionCriticalAreaReadinessExecution(readiness, false),
      error => this.completeRegionCriticalAreaReadinessExecution(readiness, true, error)
    );

    return readiness;
  }

  public getRegionCriticalAreaReadiness(
    assetId: string,
    regionId: string,
    asset?: Asset
  ): RegionCriticalAreaReadiness | undefined {
    const readiness = this.regionCriticalAreaReadiness.get(
      this.getRegionCriticalAreaReadinessKey(assetId, regionId)
    );

    return readiness && (!asset || readiness.asset === asset) ? readiness : undefined;
  }

  public clearRegionCriticalAreaReadiness(assetId: string, regionId: string, asset?: Asset): void {
    const key = this.getRegionCriticalAreaReadinessKey(assetId, regionId);
    const readiness = this.regionCriticalAreaReadiness.get(key);
    if (readiness && (!asset || readiness.asset === asset)) {
      this.regionCriticalAreaReadiness.delete(key);
    }
  }

  public clearAssetCriticalAreaReadiness(assetId: string, asset?: Asset): void {
    this.regionCriticalAreaReadiness.forEach((readiness, key) => {
      if (readiness.assetId === assetId && (!asset || readiness.asset === asset)) {
        this.regionCriticalAreaReadiness.delete(key);
      }
    });
  }

  private createRegionCriticalAreaReadiness(
    assetId: string,
    regionId: string,
    asset: Asset
  ): MutableRegionCriticalAreaReadiness {
    let resolve: () => void;
    let reject: (error: unknown) => void;
    const promise = new Promise<void>((resolvePromise, rejectPromise) => {
      resolve = resolvePromise;
      reject = rejectPromise;
    });

    // The state and original error remain observable even when no consumer is waiting yet.
    promise.catch(() => undefined);

    return {
      assetId,
      regionId,
      asset,
      generation: ++this.regionCriticalAreaReadinessGeneration,
      promise,
      state: 'loading',
      pendingExecutions: 0,
      hasFailure: false,
      resolve: resolve!,
      reject: reject!,
    };
  }

  private completeRegionCriticalAreaReadinessExecution(
    readiness: MutableRegionCriticalAreaReadiness,
    failed: boolean,
    error?: unknown
  ): void {
    if (failed && !readiness.hasFailure) {
      readiness.hasFailure = true;
      readiness.error = error;
    }

    readiness.pendingExecutions -= 1;
    if (readiness.pendingExecutions > 0) {
      return;
    }

    readiness.state = readiness.hasFailure ? 'failed' : 'ready';

    if (readiness.hasFailure) {
      readiness.reject(readiness.error);
    } else {
      readiness.resolve();
    }
  }

  private getRegionCriticalAreaReadinessKey(assetId: string, regionId: string): string {
    return `${assetId}:${regionId}`;
  }

  public startCriticalAreaCollectionMode(): void {
    this.functionalityMode = 'CRITICALAREA';
    this.graphicsRenderRepair.forceRectangleShape();
    this.graphics.Controls.enableRotate = false
    this.graphics.hammerManager.get('pan').set({ enable: true });
  }

  public startCriticalAreaByVolume(): void {
    this.functionalityMode = 'CRITICALAREAVOLUME';
    this.graphics.Controls.enableRotate = false
    this.graphics.hammerManager.get('pan').set({ enable: true });

    this.snackBar.open('Tap to insert the box to start it.', '', {
      horizontalPosition: 'center',
      verticalPosition: 'bottom',
      duration: 7000
    });
  }

  public StartCriticalAreaBoxInsertion(ev, sectionId: string): void {
    if (this.functionalityMode === 'CRITICALAREAVOLUME') {
      this.insertCriticalAreaBox(ev, sectionId)
      this.assetManager.setFunctionalityModeNone()
    }
  }

  private async insertCriticalAreaBox(ev, sectionId: string) {
    this.criticalAreaBox = this.graphicsRenderCriticalArea.createBoxOnPosition(ev, this.assetManager.asset, sectionId);
    this.templateService.showCriticalAreaBoxConfig = true

    // const dialogRef = this.dialog.open(CriticalAreaBoxConfigComponent, {
    //   panelClass: 'transparentModal',
    //   hasBackdrop: false,
    // });

    // dialogRef.afterClosed().subscribe((dimensions: Vector3) => {
    //   if (dimensions) {
    //     this.prepareToGenerateCriticalAreaByVolume(this.criticalAreaBox.position, dimensions)
    //     this.criticalAreaBox = null
    //   }
    //   else {
    //     this.criticalAreaBox = null
    //   }
    // });
  }

  handleCriticalAreaBox(dimensions: THREE.Vector3 | null) {
    if (dimensions) {
      this.prepareToGenerateCriticalAreaByVolume(this.criticalAreaBox.position, dimensions);
    }
    this.criticalAreaBox = null;
    // this.showCriticalAreaBox = false;
  }

  public prepareToGenerateCriticalAreaByVolume(position: THREE.Vector3, dimensions: THREE.Vector3) {
    var request: ICreateCriticalAreasRequestModelDTO = {
      criticalArea: {
        assetId: this.assetManager.asset.assetInfo.AssetId,
        dateStarted: this.assetManager.referenceDate,
        geometry: "BoundingBox",
        label: "CA",
        origin: "Design",
      },

      assetId: this.assetManager.asset.assetInfo.AssetId,
      regionId: this.assetManager.getCurrentRegionId(),
      sectionIds: [],
      elementIds: [],

      xMax: position.x + (dimensions.x / 2),
      yMax: position.y + (dimensions.y / 2),
      zMax: position.z + (dimensions.z / 2),

      xMin: position.x - (dimensions.x / 2),
      yMin: position.y - (dimensions.y / 2),
      zMin: position.z - (dimensions.z / 2)
    }

    var sectionRegions: string[] = []

    if (this.assetManager.asset.mode == "REGIONS") {
      // get the current region
      var visibleRegions = this.assetManager.asset.GetVisibleRegions()

      // verify the visible sections and push their ids
      for (var region of visibleRegions) {
        for (var section of region.sections) {
          if (section.visible) {
            request.sectionIds.push(section.Guid)
          }
        }
      }

    }
    else if (this.assetManager.asset.mode == "SECTIONS") {
      // get the current section id
      request.sectionIds.push(this.assetManager.currentSection.Guid)

      // verify the visible elements and push their ids
      for (var element of this.assetManager.currentSection.surfaceElements) {
        if (element.visible) {
          request.elementIds.push(element.Guid)

          for (var id of element.regionIds) {
            if (!sectionRegions.includes(id)) {
              sectionRegions.push(id)
            }
          }
        }
      }

      for (var element of this.assetManager.currentSection.linearElements) {
        if (element.visible) {
          request.elementIds.push(element.Guid)

          for (var id of element.regionIds) {
            if (!sectionRegions.includes(id)) {
              sectionRegions.push(id)
            }
          }
        }
      }
    }

    this.criticalAreaService.generateCriticalAreaByVolume(request).subscribe({
      next: (criticalAreaIds: string[]) => {
        this.criticalAreaService.regenerateCriticalAreaClusterIfNeeded2(
          request.assetId,
          sectionRegions,
          [request.criticalArea.dateStarted]
        ).subscribe({
          next: () => {
            this.assetManager.noticationService.addFinishedNotification("Creation of Critical Areas");
            this.loadRegionCriticalAreas(this.assetManager.getCurrentRegionId()).then(() => {
              this.assetManagerFilter.showCriticalAreas(criticalAreaIds);
              this.assetManager.noticationService.addLoadedNotification("Created Critical Areas");
            });
          },
          error: (err) => {
            console.log(err);
            this.assetManager.noticationService.addFailedNotification("Regenerate Cluster has");
            this.loadRegionCriticalAreas(this.assetManager.getCurrentRegionId()).then(() => {
              this.assetManagerFilter.showCriticalAreas(criticalAreaIds);
              this.assetManager.noticationService.addLoadedNotification("Created Critical Areas");
            });
          }
        });
      },
      error: (err) => {
        console.log(err);
        this.assetManager.noticationService.addFailedNotification("Failed to generate critical areas");
      },
    })
  }


  //#region Finish Insert CriticalArea
  public FinishInsertCriticalArea(selectionService: DTSelectionService): void {
    this.FinishInsertCriticalAreaModeManualCriticalArea(selectionService);
  }

  private FinishInsertCriticalAreaModeManualCriticalArea(selectionService: DTSelectionService) {
    const targetsIds: string[] = [];
    if (Util.isNullOrUndefined(this._currentCriticalAreaCampaign)) {
      const target = this.graphicsRenderRepair.rectd.targets[0];

      const _target = this.assetManager.asset.getStructureElements(false).find(x => x.Guid === target.name);
      this.addNewCriticalAreaCollection(new Date(), null, null, null, _target);
    }
    if (!Util.isNullOrUndefined(this.graphicsRenderRepair.rectd.meshs)) {
      for (const t of this.graphicsRenderRepair.rectd.targets) {
        const _t = this.assetManager.asset.getStructureElements(false).find(x => x.Guid === t.name);
        if (_t){
          targetsIds.push(_t.Guid);
        }
      }
      for (let i = 0; i < this.graphicsRenderRepair.rectd.meshs.length; i++) {
        const _mesh = this.graphicsRenderRepair.rectd.meshs[i];
        if (_mesh?.geometry instanceof THREE.BufferGeometry)
          _mesh.geometry = mergeVertices(_mesh?.geometry as THREE.BufferGeometry);
        const target = this.graphicsRenderRepair.rectd.targets[i];

        const _target = this.assetManager.asset.getStructureElements(false).find(x => x.Guid === target.name);
        if (_target) {
          let criticalAreaElement = new CriticalAreaElement(uuid(), _target, _mesh, GraphicsMaterials.criticalAreaMaterial)
          criticalAreaElement.createBorder(GraphicsMaterials.borderMaterialLineBasic);
          criticalAreaElement.CriticalAreaProperties = this.createCriticalAreaModelProperties(_target, criticalAreaElement, GraphicsRenderRepair.isSplit ? [_target.Guid] : targetsIds)

          let uvs = getUVSValues(criticalAreaElement.CriticalAreaProperties.ElementInfo.vertices, _target);

          // After adding type to mesh, the previous code gave an error. Fixing it for either Geometry or BufferGeometry
          // could have unintended side effects. Therefore, we will keep this weird check for now.
          (_mesh.geometry as any).setAttribute('uv', new THREE.BufferAttribute(uvs, 3));
          (_mesh.geometry as any).setAttribute('position', new THREE.BufferAttribute(new Float32Array(criticalAreaElement.CriticalAreaProperties.ElementInfo.vertices), 3));

          if (_target instanceof SurfaceElement) {
            this._currentCriticalAreaCampaign.addSurfaceElement(criticalAreaElement);
          }

          if (_target instanceof LinearElement) {
            this._currentCriticalAreaCampaign.addLinearElement(criticalAreaElement, _target);
          }


          this.saveCriticalArea(criticalAreaElement.CriticalAreaProperties)

          // Register for undo after save
          // this.criticalAreaCommandService.registerInsertCommand(criticalAreaElement);

          selectionService.clearElementSelected('', this.assetManager.asset)
          selectionService.markElementSelected(criticalAreaElement.Guid, this.assetManager.asset)
        }

        // if (graphicsRenderRepair.isSplit) {
        //   _CriticalArea = new CriticalArea(false, this.graphicsRenderRepair.rectd.targets, _mesh, null, undefined, undefined, this.graphicsRender.lastCameraSignal);
        //   this.currentCriticalAreaCollection.addCriticalArea(_CriticalArea);
        //   if (!Util.isNullOrUndefined(_CriticalArea)) {
        //     this.collectionService.addElement(_CriticalArea, this.currentCriticalAreaCollection.Date, false);
        //   }
        // } else {

        // }
      }

      this.graphicsRenderRepair.rectd.meshs = []
      this.graphicsRenderRepair.rectd.targets = []
      // The new critical areas' createBorder above: follow the "show section borders" setting
      this.applyCriticalAreaBorderSetting();
    }


  }

  /**
   * Applies the "show section borders" setting to the critical-area element borders
   * (Asset.applyCriticalAreaBorderVisibility); renders when a border changed.
   */
  private applyCriticalAreaBorderSetting(): void {
    const asset = this.assetManager?.asset;
    if (!asset) return;
    if (asset.applyCriticalAreaBorderVisibility() > 0) this.graphics?.requestRender();
  }

  saveCriticalArea(criticalAreaProperties: CriticalAreaModel) {
    let criticalAreaDTO = this.setCriticalAreaDTO(criticalAreaProperties)
    let criticalAreaElementDTO = this.setCriticalAreaElementDTO(criticalAreaProperties, criticalAreaDTO)
    let criticalAreaElementDTOArray: ICriticalAreaElementDTO[] = []
    criticalAreaElementDTOArray.push(criticalAreaElementDTO)

    // console.log(criticalAreaDTO)
    // console.log(criticalAreaElementDTO)
    this.criticalAreaService.postCriticalArea(criticalAreaDTO, criticalAreaElementDTOArray).subscribe(data => {
      // console.log(data)
    })
  }


  setCriticalAreaDTO(criticalAreaProperties: CriticalAreaModel): ICriticalAreaDTO {
    return {
      id: criticalAreaProperties.CriticalAreaId ? criticalAreaProperties.CriticalAreaId : uuid() as string,
      assetId: criticalAreaProperties.AssetId,
      dateEnded: criticalAreaProperties.DateEnded,
      dateStarted: criticalAreaProperties.DateStarted,
      geometry: criticalAreaProperties.Geometry,
      origin: criticalAreaProperties.Origin,
      reasons: criticalAreaProperties.Reasons,
      regionIds: criticalAreaProperties.RegionIds,
      label: 'CA',
      TargetGeometryTypeId: criticalAreaProperties.TargetGeometryTypeId,
    }
  }

  setCriticalAreaElementDTO(criticalAreaProperties: CriticalAreaModel, criticalArea: ICriticalAreaDTO): ICriticalAreaElementDTO {
    return {
      Id: criticalAreaProperties.Id,
      CriticalAreaId: criticalArea.id,
      assetId: criticalAreaProperties.AssetId,
      Element: null,
      regionId: criticalAreaProperties.RegionId,
      regionIds: criticalAreaProperties.RegionIds,
      sectionId: criticalAreaProperties.SectionId,
      TargetGeometryIds: criticalAreaProperties.TargetGeometryIds,
      TargetGeometryTypeId: criticalAreaProperties.TargetGeometryTypeId,
      ElementInfo: criticalAreaProperties.ElementInfo,
    }
  }


  createCriticalAreaModelProperties(_target: any, criticalAreaElement: any, targetsId: string[]): CriticalAreaModel {

    let vertices = []
    let faces = []

    criticalAreaElement.SurfaceDesign.geometry.attributes.position.array.forEach((v) => {
      vertices.push(v);
    });

    criticalAreaElement.SurfaceDesign.geometry.index.array.forEach((i) => {
      faces.push(i);
    });

    let criticalAreaProperties: CriticalAreaModel = {
      Id: uuid() as string,
      Element: null,
      AssetId: this.assetManager.asset.assetInfo.AssetId,
      RegionId: _target.regionId,
      RegionIds: _target.regionIds,
      Origin: 'Design',
      Label: null,
      Reasons: null,
      Geometry: 'Area',
      ElementInfo: {
        faces: faces,
        type: 'b976ab089c794d10ac2547dde1d6a370',
        vertices: vertices,
      },
      SectionId: _target.SectionId,
      TargetGeometryIds: targetsId,
      TargetGeometryTypeId: _target.TypeId,
      CriticalAreaId: uuid() as string,
      CriticalAreaElementIds: [criticalAreaElement.Id],
      DateStarted: this.assetManager.referenceDate,
      DateEnded: null,
    }
    return criticalAreaProperties;
  }

  setCriticalAreaModelPropertiesDownloaded(_target: any, criticalAreaElement: ICriticalAreaElementDTO, criticalAreas: ICriticalAreaDTO[]): CriticalAreaModel {
    for (let index = 0; index < criticalAreas.length; index++) {
      const criticalArea = criticalAreas[index];
      if (criticalArea.id == criticalAreaElement.CriticalAreaId) {
        let criticalAreaProperties: CriticalAreaModel = {
          Id: criticalAreaElement.Id,
          CriticalAreaId: criticalAreaElement.CriticalAreaId,
          CriticalAreaElementIds: criticalArea.criticalAreaElementIds,
          Element: null,
          AssetId: criticalAreaElement.assetId,
          RegionId: criticalAreaElement.regionId,
          RegionIds: criticalAreaElement.regionIds,
          Origin: criticalArea.origin,
          Reasons: criticalArea.reasons,
          Label: criticalArea.label,
          Geometry: criticalArea.geometry,
          ElementInfo: {
            faces: criticalAreaElement.ElementInfo.faces,
            type: criticalAreaElement.ElementInfo.type,
            vertices: criticalAreaElement.ElementInfo.vertices,
          },
          SectionId: criticalAreaElement.sectionId,
          TargetGeometryIds: criticalAreaElement.TargetGeometryIds,
          TargetGeometryTypeId: criticalAreaElement.TargetGeometryTypeId,
          DateStarted: new Date(criticalArea.dateStarted),
          DateEnded: new Date(criticalArea.dateEnded),
          CriticalAreaActions: criticalArea.CriticalAreaActions,
          RepairIds: criticalArea.RepairIds,
          InspectionIds: criticalArea.InspectionIds
        }
        return criticalAreaProperties;
      }
    }
    return null;
  }

  public async loadRegionCriticalAreas(regionId: string) {
    // console.log("loading Region CriticalAreas")
    const res = await this.digitalTwinService.getCriticalAreaElements(regionId, this.assetManager.asset.assetInfo.AssetId);
    if (!res) {
      return;
    }
    const { criticalAreas, criticalAreaElements } = res;

    if (!criticalAreas || criticalAreaElements.length === 0)
      return;
    if (Util.isNullOrUndefined(this._currentCriticalAreaCampaign)) {
      this.addNewCriticalAreaCollection(new Date(), null, null, null);
    }

    const structuralElements = this.assetManager.asset.getStructureElements(false)

    for (const criticalAreaElement of criticalAreaElements) {

      const _isAddedcriticialAreas = this._currentCriticalAreaCampaign.Elements.find(x => x.Guid === criticalAreaElement.Id);
      if (_isAddedcriticialAreas)
        continue;

      const targetId = criticalAreaElement.TargetGeometryIds[0]
      const targetId2 = criticalAreaElement.TargetGeometryIds[0].replace(/-/g, "")
      const _target = structuralElements.find(x => x.Guid === targetId || x.Guid === targetId2);
      if (_target) {
        const geometry = new THREE.BufferGeometry();
        if (!criticalAreaElement.ElementInfo) return;

        let uvs = getUVSValues(criticalAreaElement.ElementInfo.vertices, _target);
        geometry.setAttribute('uv', new THREE.BufferAttribute(uvs, 3));
        geometry.setAttribute('position', new THREE.BufferAttribute(new Float32Array(criticalAreaElement.ElementInfo.vertices), 3));

        if (criticalAreaElement.ElementInfo.faces && criticalAreaElement.ElementInfo.faces.length > 0) geometry.setIndex(criticalAreaElement.ElementInfo.faces);
        geometry.computeVertexNormals();

        let material: THREE.Material = GraphicsMaterials.criticalAreaMaterial;

        const _mesh = new THREE.Mesh(geometry, material);

        let criticalArea = criticalAreas.find(x => x.id == criticalAreaElement.CriticalAreaId)

        let newMat = await this.getCriticalAreaMaterial(criticalArea);
        if (!newMat) {
          console.warn(`No material created for critical area ${criticalArea.id}`);
          newMat = GraphicsMaterials.criticalAreaMaterial
        }

        let criticalAreaElementVisual = new CriticalAreaElement(uuid(), _target, _mesh, newMat)
        criticalAreaElementVisual.createBorder(GraphicsMaterials.borderMaterialLineBasic);
        criticalAreaElementVisual.CriticalAreaProperties = this.setCriticalAreaModelPropertiesDownloaded(_target, criticalAreaElement, criticalAreas)
        criticalAreaElementVisual.SurfaceDesign.visible = false
        this._currentCriticalAreaCampaign.addSurfaceElement(criticalAreaElementVisual);
      }
    }
    // The loaded critical areas' createBorder above: follow the "show section borders" setting
    // (MODEL/REGIONS per the setting, always shown in SECTIONS / old format)
    this.applyCriticalAreaBorderSetting();
    this.assetManager.noticationService.addLoadedNotification("Critical Areas")
    this.assetManager.updateViewMode();
  }

  private addNewCriticalAreaCollection(
    pDate?: Date,
    CurrentMethodId?: string,
    CurrentInspectionTypeId?: string,
    InpectionTypeName?: string,
    target?: WorkableElement
  ): CriticalAreaCampaign {
    let _newCollection = new CriticalAreaCampaign(
      this.assetManager.AddCollectionIntegration(
        pDate,
        null,
        CollectionStaticData.COLLECTION_TYPE_INSPECTION_ID,
        CurrentMethodId,
        CurrentInspectionTypeId,
        InpectionTypeName,
        false
      ),
      pDate,
    )
    this.assetManager.asset.addCriticalAreaCollection(_newCollection)
    this._currentCriticalAreaCampaign = _newCollection
    return _newCollection
  }

  async getCriticalAreaMaterial(criticalArea: ICriticalAreaDTO): Promise<THREE.MeshBasicMaterial> {
    try {

      if (new Date(criticalArea.dateEnded).getFullYear() > 1 ) {
        return GraphicsMaterials.criticalAreaMaterial
      }
      const customerId = this.assetManager.asset.assetInfo.CustomerId;

      // Get risk matrix data
      const fullriskMatrix = await firstValueFrom(
        this.riskMatrixService.fetchCachedRiskMatrixData(customerId)
      );

      // Calculate risk level
      const highestReason = riskMatrixUtils.getHighestReason(
        criticalArea.reasons,
        fullriskMatrix.riskMatrixData,
        fullriskMatrix.riskLevelItems,
        fullriskMatrix.probabilityOfFailureItems,
        fullriskMatrix.consequenceItems,
      );

      const riskLevel = riskMatrixUtils.getRiskMatrixCell(highestReason, fullriskMatrix.riskMatrixData);
      const material = GraphicsMaterials.getRiskMaterial(
        riskLevel?.riskLevelId,
        fullriskMatrix.riskLevelItems
      );

      return material || GraphicsMaterials.criticalAreaMaterial;
    } catch (error) {
      return GraphicsMaterials.criticalAreaMaterial;
    }
  }

  /**
   * Updates critical area properties for a specific critical area element
   * @param criticalAreaId - The ID of the critical area to update
   * @param updatedProperties - Partial properties to update
   * @param saveToBackend - Whether to save changes to backend (default: false)
   * @returns Promise<boolean> - Success status
   */
  public async updateCriticalAreaProperties(
    criticalAreaId: string, 
    updatedProperties: Partial<CriticalAreaModel>,
    saveToBackend: boolean = false
  ): Promise<boolean> {
    try {
      // Find the critical area element in the campaigns
      let targetCriticalAreaElement: CriticalAreaElement | null = null;
      let targetCampaign: CriticalAreaCampaign | null = null;

      // Search through all critical area campaigns
      for (const campaign of this.assetManager.asset.CriticalAreaCampaigns) {
        for (const element of campaign.Elements) {
          const criticalAreaElement = element as CriticalAreaElement;
          if (criticalAreaElement.CriticalAreaProperties?.CriticalAreaId === criticalAreaId) {
            targetCriticalAreaElement = criticalAreaElement;
            targetCampaign = campaign;
            break;
          }
        }
        if (targetCriticalAreaElement) break;
      }

      if (!targetCriticalAreaElement || !targetCampaign) {
        console.warn(`Critical area with ID ${criticalAreaId} not found in campaigns`);
        return false;
      }

      // Update the properties
      const currentProperties = targetCriticalAreaElement.CriticalAreaProperties;
      const updatedCriticalAreaProperties: CriticalAreaModel = {
        ...currentProperties,
        ...updatedProperties
      };

      // Update the element's properties
      targetCriticalAreaElement.CriticalAreaProperties = updatedCriticalAreaProperties;

      // Update visual material if reasons were changed
      if (updatedProperties.Reasons) {
        const criticalAreaDTO = this.setCriticalAreaDTO(updatedCriticalAreaProperties);
        const newMaterial = await this.getCriticalAreaMaterial(criticalAreaDTO);
        if (newMaterial) {
          targetCriticalAreaElement.SurfaceDesign.material = newMaterial;
          targetCriticalAreaElement.defaultMaterial = newMaterial;
        }
      }

      // Update other elements with the same critical area ID
      this.updateRelatedCriticalAreaElements(criticalAreaId, updatedCriticalAreaProperties);

      // Save to backend if requested
      if (saveToBackend) {
        await this.saveCriticalAreaToBackend(updatedCriticalAreaProperties);
      }

      console.log(`Successfully updated critical area ${criticalAreaId}`);
      return true;

    } catch (error) {
      console.error('Error updating critical area properties:', error);
      return false;
    }
  }

  /**
   * Updates all critical area elements that share the same critical area ID
   * @param criticalAreaId - The critical area ID to match
   * @param updatedProperties - The updated properties to apply
   */
  private updateRelatedCriticalAreaElements(criticalAreaId: string, updatedProperties: CriticalAreaModel): void {
    for (const campaign of this.assetManager.asset.CriticalAreaCampaigns) {
      for (const element of campaign.Elements) {
        const criticalAreaElement = element as CriticalAreaElement;
        if (criticalAreaElement.CriticalAreaProperties?.CriticalAreaId === criticalAreaId) {
          // Update shared properties
          criticalAreaElement.CriticalAreaProperties.Label = updatedProperties.Label;
          criticalAreaElement.CriticalAreaProperties.Reasons = updatedProperties.Reasons;
          criticalAreaElement.CriticalAreaProperties.Origin = updatedProperties.Origin;
          criticalAreaElement.CriticalAreaProperties.Geometry = updatedProperties.Geometry;
          criticalAreaElement.CriticalAreaProperties.DateStarted = updatedProperties.DateStarted;
          criticalAreaElement.CriticalAreaProperties.DateEnded = updatedProperties.DateEnded;
          criticalAreaElement.CriticalAreaProperties.CriticalAreaActions = updatedProperties.CriticalAreaActions;
          criticalAreaElement.CriticalAreaProperties.RepairIds = updatedProperties.RepairIds;
          criticalAreaElement.CriticalAreaProperties.InspectionIds = updatedProperties.InspectionIds;
        }
      }
    }
  }

  /**
   * Saves critical area properties to the backend
   * @param criticalAreaProperties - The critical area properties to save
   */
  private async saveCriticalAreaToBackend(criticalAreaProperties: CriticalAreaModel): Promise<void> {
    try {
      const criticalAreaDTO = this.setCriticalAreaDTO(criticalAreaProperties);
      const criticalAreaElementDTO = this.setCriticalAreaElementDTO(criticalAreaProperties, criticalAreaDTO);
      const criticalAreaElementDTOArray: ICriticalAreaElementDTO[] = [criticalAreaElementDTO];

      await firstValueFrom(
        this.criticalAreaService.postCriticalArea(criticalAreaDTO, criticalAreaElementDTOArray)
      );

      console.log('Critical area saved to backend successfully');
    } catch (error) {
      console.error('Error saving critical area to backend:', error);
      throw error;
    }
  }

}
